import { now } from '../Clock.ts';
import { Rng } from '../game/Rng.ts';
import type { SimulatedEmgDevice } from '../hardware/SimulatedEmgDevice.ts';
import type { VirtualButtonDevice } from '../hardware/VirtualButtonDevice.ts';
import {
  BaseStimulator,
  type StimulatorKind,
  type StimulusRequest,
  type StimulusResult,
} from './Stimulator.ts';

/**
 * Parameters of the simulated participant.
 *
 * These are the knobs the whole delay-aware-control experiment is built to
 * explore. Nothing here claims to be a physiological model: it is a delay with
 * jitter, a failure rate, and a refractory period, which is the minimum needed
 * to make a controller confront a slow, unreliable actuator.
 */
export interface HumanResponseModel {
  /** Mean time from stimulus onset to detectable movement, ms. */
  meanLatencyMs: number;
  /** Standard deviation of that latency, ms. */
  latencyStdDevMs: number;
  /** Floor on sampled latency, ms. Physiology has no negative tail. */
  minLatencyMs: number;
  /** Probability that a stimulus produces a detectable movement at all. */
  successProbability: number;
  /**
   * Rate of spurious button events unrelated to any stimulus, per second.
   * Models a twitchy participant, a loose contact, or a badly placed switch.
   */
  falsePositiveProbability: number;
  /**
   * Time after a movement during which a further stimulus produces nothing.
   * Distinct from `SafetyLimits.minIntervalMs`: that one stops the software
   * from asking, this one is the participant not being able to answer.
   */
  refractoryPeriodMs: number;
  /**
   * How long the contact stays closed, ms.
   *
   * Not cosmetic: while the contact is closed a second movement cannot be
   * detected, so a hold longer than the interval between stimulation requests
   * silently swallows responses. That is true of a real switch too, which is
   * why it is modelled rather than assumed away.
   */
  buttonHoldMs: number;
  seed: number;
}

export const DEFAULT_HUMAN_MODEL: HumanResponseModel = {
  meanLatencyMs: 80,
  latencyStdDevMs: 20,
  minLatencyMs: 25,
  successProbability: 0.95,
  falsePositiveProbability: 0.0,
  refractoryPeriodMs: 120,
  buttonHoldMs: 40,
  seed: 7,
};

interface ScheduledResponse {
  requestId: string;
  pressAt: number;
  releaseAt: number;
  pressed: boolean;
}

/**
 * A stimulator wired to a simulated participant.
 *
 * The chain it implements is exactly the one the real setup has:
 *
 *   requestStimulus
 *     -> pulse start / end reported immediately (the device is fast)
 *     -> sample: does this participant respond at all?          [successProbability]
 *     -> sample: how long do they take?                [meanLatency +/- stdDev]
 *     -> at that time, press a VirtualButtonDevice
 *     -> (optionally) emit an EMG burst at the same instant
 *
 * The crucial detail is the last two steps. This class never tells the game
 * anything. It presses a button, and the controller finds out the same way it
 * would find out about a real switch closing under a real wrist. Replacing this
 * object with `BackyardBrainsHHIAdapter` and the virtual button with
 * `SerialInput` changes nothing above it.
 *
 * Time comes from `pump(nowMs)`, called once per frame, rather than from
 * `setTimeout`. That keeps responses aligned with the simulation clock, makes
 * headless trial runs possible at thousands of times realtime, and keeps a
 * session reproducible from its seed.
 */
export class SimulatedHumanStimulator extends BaseStimulator {
  readonly kind: StimulatorKind = 'simulated-human';
  readonly label = 'Simulated human (no hardware)';
  readonly simulated = true;

  model: HumanResponseModel;

  private rng: Rng;
  private queue: ScheduledResponse[] = [];
  private lastMovementAt = -Infinity;
  private lastFalsePositiveCheck: number | null = null;
  private missedCount = 0;
  private falsePositiveCount = 0;
  private movementCount = 0;

  constructor(
    private readonly button: VirtualButtonDevice,
    model: Partial<HumanResponseModel> = {},
    private readonly emg?: SimulatedEmgDevice,
  ) {
    super();
    this.model = { ...DEFAULT_HUMAN_MODEL, ...model };
    this.rng = new Rng(this.model.seed);
  }

  async connect(): Promise<void> {
    this.connected = true;
    await this.button.connect();
  }

  async disconnect(): Promise<void> {
    this.connected = false;
    this.armed = false;
    this.queue = [];
  }

  /** Re-seed and clear all state. Call between trials for reproducibility. */
  reset(seed = this.model.seed): void {
    this.rng = new Rng(seed);
    this.queue = [];
    this.lastMovementAt = -Infinity;
    this.lastFalsePositiveCheck = null;
    this.missedCount = 0;
    this.falsePositiveCount = 0;
    this.movementCount = 0;
    this.requestCount = 0;
    this.acceptedCount = 0;
    this.rejectedCount = 0;
    this.deliveredCount = 0;
  }

  async requestStimulus(request: StimulusRequest): Promise<StimulusResult> {
    const denied = this.guard(request);
    if (denied) return denied;

    this.requestCount++;
    this.acceptedCount++;
    this.deliveredCount++;
    this.lastRequestAt = request.requestedAt;

    const startedAt = now();
    this.lastStimulusAt = startedAt;

    // Does the participant respond to this one?
    const inRefractory = startedAt - this.lastMovementAt < this.model.refractoryPeriodMs;
    const responds = !inRefractory && this.rng.bernoulli(this.model.successProbability);

    if (responds) {
      const latency = Math.max(
        this.model.minLatencyMs,
        this.rng.normal(this.model.meanLatencyMs, this.model.latencyStdDevMs),
      );
      this.queue.push({
        requestId: request.requestId,
        pressAt: startedAt + latency,
        releaseAt: startedAt + latency + this.model.buttonHoldMs,
        pressed: false,
      });
    } else {
      this.missedCount++;
    }

    return {
      requestId: request.requestId,
      accepted: true,
      startedAt,
      endedAt: startedAt + request.durationMs,
      // The caller must NOT read this as "a flap happened". It means the
      // stimulator fired. Whether a movement follows is what `pump` decides.
    };
  }

  /**
   * Advance the simulated participant to `nowMs`, firing any due movements.
   * Called once per animation frame (or once per tick in headless trials).
   */
  pump(nowMs: number): void {
    for (const r of this.queue) {
      if (!r.pressed && nowMs >= r.pressAt) {
        r.pressed = true;
        this.lastMovementAt = r.pressAt;
        this.movementCount++;
        this.button.press(r.pressAt, r.requestId, { simulated: true });
        this.emg?.burst(r.pressAt);
      }
      if (r.pressed && nowMs >= r.releaseAt) {
        this.button.release(r.releaseAt, r.requestId);
      }
    }
    this.queue = this.queue.filter((r) => !(r.pressed && nowMs >= r.releaseAt));

    this.pumpFalsePositives(nowMs);
  }

  /**
   * Spontaneous movements unrelated to any stimulus.
   *
   * Modelled as a Poisson process, sampled over the elapsed interval so the
   * rate does not depend on frame rate. These presses carry no requestId, which
   * is what lets the analysis separate "the participant responded" from "the
   * participant twitched" -- and what makes the flap they cause show up in the
   * log as an unattributed FLAP_EXECUTED.
   */
  private pumpFalsePositives(nowMs: number): void {
    const rate = this.model.falsePositiveProbability;
    if (rate <= 0) {
      this.lastFalsePositiveCheck = nowMs;
      return;
    }
    if (this.lastFalsePositiveCheck === null) {
      this.lastFalsePositiveCheck = nowMs;
      return;
    }
    const elapsedSec = (nowMs - this.lastFalsePositiveCheck) / 1000;
    this.lastFalsePositiveCheck = nowMs;
    if (elapsedSec <= 0) return;

    const p = 1 - Math.exp(-rate * elapsedSec);
    if (this.rng.bernoulli(p)) {
      if (nowMs - this.lastMovementAt < this.model.refractoryPeriodMs) return;
      this.lastMovementAt = nowMs;
      this.falsePositiveCount++;
      this.button.press(nowMs, undefined, { falsePositive: true });
      this.button.release(nowMs + this.model.buttonHoldMs, undefined);
      this.emg?.burst(nowMs);
    }
  }

  get counters(): { movements: number; missed: number; falsePositives: number } {
    return {
      movements: this.movementCount,
      missed: this.missedCount,
      falsePositives: this.falsePositiveCount,
    };
  }

  protected override detail(): Record<string, unknown> {
    return {
      meanLatencyMs: this.model.meanLatencyMs,
      latencyStdDevMs: this.model.latencyStdDevMs,
      successProbability: this.model.successProbability,
      refractoryPeriodMs: this.model.refractoryPeriodMs,
      pendingResponses: this.queue.length,
      ...this.counters,
    };
  }
}
